import { useCallback, useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { ProductCategoryTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import ProductCategories, { ProductState } from "./ProductCategories.web";

export interface ProductCategoriesListProps {
  eventData: PublicBaseEventSerializer;
  productCategories: ProductCategoryTypeSerializer[];
  isLoading: boolean;
}

const ProductCategoriesList = ({
  eventData,
  productCategories,
  isLoading,
}: ProductCategoriesListProps) => {
  const { t } = useTranslation("purchases");
  const [categoryStateMap, setCategoryStateMap] = useState<
    Record<number, ProductState>
  >({});
  const hasNoProducts =
    Object.values(categoryStateMap).filter((v) => v === ProductState.EMPTY)
      .length === productCategories.length;

  // make a stable function to avoid infinite re-renders
  const updateCategoryStateMap = useCallback(
    (categoryId: number, productState: ProductState) => {
      setCategoryStateMap((prev) => ({
        ...prev,
        [categoryId]: productState,
      }));
    },
    [setCategoryStateMap]
  );

  if (hasNoProducts) {
    return (
      <Flex
        width="100%"
        justifyContent="flex-start"
        direction="column"
        gap="md"
      >
        <Text>{t("No products available for purchase")}</Text>
      </Flex>
    );
  }

  return (
    <Flex width="100%" justifyContent="flex-start" direction="column" gap="md">
      {productCategories.length > 0 &&
        productCategories.map((productCategory) => {
          return (
            <Skeleton
              isLoaded={
                categoryStateMap[productCategory.id] !== ProductState.LOADING
              }
              key={productCategory.id}
            >
              <Flex
                key={productCategory.id}
                width="100%"
                direction="column"
                display={
                  categoryStateMap[productCategory.id] === ProductState.EMPTY
                    ? "none"
                    : "flex"
                }
              >
                <ProductCategories
                  onStateChange={updateCategoryStateMap}
                  key={productCategory.id}
                  categoryId={productCategory.id}
                  categoryName={productCategory.name}
                  eventData={eventData}
                />
              </Flex>
            </Skeleton>
          );
        })}
      {(!productCategories || productCategories.length === 0) && !isLoading && (
        <Flex fontSize={{ base: "3xl", sm: "lg" }} fontWeight="bold">
          <Text>{t("No product categories found.")}</Text>
        </Flex>
      )}
      {isLoading && (
        <>
          <Skeleton
            borderRadius="var(--chakra-radii-md)"
            width="100%"
            // TODO: Add element token for 56px (between element.4xl=48px and element.5xl=64px)
            // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
            height={{ base: "56px", sm: "element.4xl" }}
          />
          <Skeleton
            mt="2xl"
            borderRadius="var(--chakra-radii-md)"
            width="100%"
            // TODO: Add element token for 56px (between element.4xl=48px and element.5xl=64px)
            // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
            height={{ base: "56px", sm: "element.4xl" }}
          />
          <Skeleton
            mt="2xl"
            borderRadius="var(--chakra-radii-md)"
            width="100%"
            // TODO: Add element token for 56px (between element.4xl=48px and element.5xl=64px)
            // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
            height={{ base: "56px", sm: "element.4xl" }}
          />
        </>
      )}
    </Flex>
  );
};

export default ProductCategoriesList;
